import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { classNames } from '@/utils';

interface Size {
    width: number | null;
    height: number | null;
}

type SizeMeProps = {
    children: (props: { size: Size }) => ReactNode;
    className?: string;
    style?: React.CSSProperties;

    monitorWidth?: boolean;
    monitorHeight?: boolean;
    refreshRate?: number;
    refreshMode?: 'throttle' | 'debounce';
    noPlaceholder?: boolean;
};

export const SizeMe = ({
    children,
    className,
    style,
    monitorWidth = true,
    monitorHeight = false,
    refreshRate = 16,
    refreshMode = 'throttle',
    noPlaceholder = false
}: SizeMeProps) => {
    const [size, setSize] = useState<Size>({ width: null, height: null });
    const [measured, setMeasured] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<number | null>(null);

    const updateSize = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const next: Size = {
            width: monitorWidth ? Math.round(rect.width) : null,
            height: monitorHeight ? Math.round(rect.height) : null
        };
        setSize((prev) => {
            const changed =
                (monitorWidth && prev.width !== next.width) ||
                (monitorHeight && prev.height !== next.height);
            return changed ? next : prev;
        });
    }, [monitorWidth, monitorHeight]);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        if (!noPlaceholder) updateSize();
        setMeasured(true);
    }, [noPlaceholder, updateSize]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            if (refreshMode === 'throttle') {
                if (timerRef.current) return;
                updateSize();
                timerRef.current = window.setTimeout(() => {
                    timerRef.current = null;
                }, refreshRate);
            } else {
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = window.setTimeout(() => {
                    updateSize();
                    timerRef.current = null;
                }, refreshRate);
            }
        });

        observer.observe(el);

        return () => {
            observer.disconnect();
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [updateSize, refreshRate, refreshMode]);

    const containerProps = {
        ref: containerRef,
        className: classNames({ 'w-full': monitorWidth, 'h-full': monitorHeight }, className),
        style
    };

    return !measured && !noPlaceholder ? (
        <div {...containerProps} />
    ) : (
        <div {...containerProps}>{children({ size })}</div>
    );
};
